import React, { createContext, useState, useCallback, useEffect, useMemo } from 'react';
import { GEO_RESOLVE_ENDPOINT } from '~/features/location/server/resolve-location';

export const GeoLocationContext = createContext({
  location: '',
  updateLocation: () => {},
});

const normalizeCity = (payload) => {
  const city = payload?.match?.city;
  return typeof city === 'string' ? city.trim() : '';
};

export const GeoLocationProvider = ({ children, ssrLocation = '' }) => {
  const [location, setLocation] = useState(ssrLocation || '');

  useEffect(() => {
    if (typeof ssrLocation === 'string' && ssrLocation.trim()) {
      setLocation(ssrLocation.trim());
    }
  }, [ssrLocation]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if ((ssrLocation || '').trim()) {
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);

    fetch(GEO_RESOLVE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: window.location.href }),
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        const city = normalizeCity(payload);
        if (city) {
          setLocation(city);
        }
      })
      .catch(() => null)
      .finally(() => clearTimeout(timeout));

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [ssrLocation]);

  const updateLocation = useCallback((value) => {
    let city = '';

    if (typeof value === 'string') city = value.trim();
    else if (value?.city) city = value.city.trim();
    else if (value?.name) city = value.name.trim();

    if (city) {
      setLocation(city);
    }
  }, []);

  const value = useMemo(() => ({ location, updateLocation }), [location, updateLocation]);

  return <GeoLocationContext.Provider value={value}>{children}</GeoLocationContext.Provider>;
};
